import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Card, Space, Button, Typography, Tooltip } from "@arco-design/web-react";

import { PersonCardProps } from "../models/Person";
import { UserAvatar } from "./UserAvatar/UserAvatar";
import "./PersonCard.css";
import { routes } from "../routes/routes";
import { RootState } from "../store/store";
import { getButtonColor, getButtonTextColor } from "../utils/cssVariables";
import { getLanguageSpokenLabel } from "../utils/languageSpokenLabel";

const BIO_ABBREV_ONLY = /^(Dr|Mr|Mrs|Ms|Prof|Sr|Jr|St)\.$/i;

/** First sentence (or lead) for card preview; keeps featured cards visually even. */
function bioSnippetOneSentence(fullBio: string | undefined): string {
  if (!fullBio?.trim()) return "";
  const t = fullBio.replace(/\s+/g, " ").trim();
  const parts = t.split(/(?<=[.!?])\s+/);
  let snippet = "";

  for (const part of parts) {
    const segment = part.trim();
    if (!segment) continue;

    snippet = snippet ? `${snippet} ${segment}` : segment;

    if (BIO_ABBREV_ONLY.test(segment)) {
      continue;
    }

    if (snippet.length >= 40 || /[.!?]$/.test(snippet)) {
      return snippet.length > 320 ? `${snippet.slice(0, 317)}…` : snippet;
    }
  }

  const fallback = snippet || t;
  return fallback.length > 320 ? `${fallback.slice(0, 317)}…` : fallback;
}

export const PersonCard = (props: PersonCardProps) => {
  const { t } = useTranslation();
  const useNycIntake = useSelector((state: RootState) => state.config.useNycIntake);

  const user = props.person;
  const languageLabel = getLanguageSpokenLabel(user?.language, t, useNycIntake);
  const bioLead = bioSnippetOneSentence(user?.fullBio);
  const profilePath =
    user.isMentor || user.isAdmin
      ? routes.MENTOR.replace(":id", user.id.toString())
      : routes.MENTEE.replace(":id", user.id.toString());

  return (
    <Card
      className="card-with-icon-hover person-card"
      bodyStyle={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
      cover={
        <div className="person-card__cover">
          <div className="person-card__cover-inner">
            <UserAvatar
              avatarUrl={user?.avatar_url}
              userId={user?.id}
              firstName={user.name}
              lastName={user.last_name}
              shape="square"
              alt="avatar"
            />
          </div>
        </div>
      }
    >
      <div
        className="person-card__body"
        style={{
          textAlign: "left",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="person-card__body-main">
          <Typography.Title heading={4} className="person-card__name">{user.name} {user.last_name}</Typography.Title>
          <p className="person-card__title-text">{user?.shortDescription}</p>
          <div className="person-card__language" aria-hidden={!languageLabel}>
            {languageLabel ?? "\u00a0"}
          </div>
          <div className="person-card__bio-snippet">
            {bioLead ? (
              <Tooltip
                content={
                  <div className="person-card__bio-tooltip">
                    {user.fullBio}
                  </div>
                }
                position="tl"
              >
                <p className="person-card__bio-text">{bioLead}</p>
              </Tooltip>
            ) : (
              <span className="person-card__bio-placeholder" aria-hidden />
            )}
          </div>
        </div>
        <div className="person-card__actions">
          <Space>
            <NavLink
              to={profilePath}
              state={{
                profilePreview: {
                  avatar_url: user.avatar_url,
                  name: user.name,
                  last_name: user.last_name,
                  shortDescription: user.shortDescription,
                },
              }}
            >
              <Button
                style={{
                  color: getButtonTextColor(),
                  backgroundColor: getButtonColor(),
                }}
                size={"large"}
                shape={"round"}>
                {t('common.view_mentor_profile_button')}
              </Button>
            </NavLink>
          </Space>
        </div>
      </div>
    </Card>
  );
};
